<template>
  <form action="" @submit.prevent="submitSubCategoryData">
    <div class="p-8 sm:p-6">
      <sw-input-group
          :label="$t('settings.expense_subcategory.subcategory_master')"
          class="mt-4"
          variant="horizontal"
          required
        >
          <sw-select
            v-model="master"
            :options="categories"
            :searchable="true"
            :show-labels="false"
            :placeholder="$t('settings.expense_subcategory.subcategory_master')"
            :allow-empty="false"
            track-by="id"
            label="formattedCategory"
            class="mt-1 md:mt-0"
          />
      </sw-input-group>

      <sw-input-group
        :label="$t('settings.expense_subcategory.subcategory_code')"
        :error="codeError"
        variant="horizontal"
        class="mt-5"
        required
      >
        <sw-input
          ref="code"
          :invalid="$v.formData.code.$error"
          v-model="formData.code"
          type="text"
          @input="$v.formData.code.$touch()"
        />
      </sw-input-group>
      
      <sw-input-group
        :label="$t('expenses.category')"
        :error="nameError"
        class="mt-5"
        variant="horizontal"
        required
      >
        <sw-input
          ref="name"
          :invalid="$v.formData.name.$error"
          v-model="formData.name"
          type="text"
          @input="$v.formData.name.$touch()"
        />
      </sw-input-group>

      <sw-input-group
        :label="$t('expenses.description')"
        :error="descriptionError"
        class="mt-5"
        variant="horizontal"
      >
        <sw-textarea
          v-model="formData.description"
          rows="4"
          cols="50"
          @input="$v.formData.description.$touch()"
        />
      </sw-input-group>
    </div>
    <div
      class="z-0 flex justify-end p-4 border-t border-gray-200 border-solid border-modal-bg"
    >
      <sw-button
        type="button"
        variant="primary-outline"
        class="mr-3 text-sm"
        @click="closeSubcategoryModal"
      >
        {{ $t('general.cancel') }}
      </sw-button>
      <sw-button variant="primary" type="submit" :loading="isLoading">
        <save-icon v-if="!isLoading" class="mr-2" />
        {{ !isEdit ? $t('general.save') : $t('general.update') }}
      </sw-button>
    </div>
  </form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
const { required, minLength, maxLength } = require('vuelidate/lib/validators')
export default {
  data() {
    return {
      isEdit: false,
      isLoading: false,
      master: null,
      formData: {
        id: null,
        code: null,
        name: null,
        description: null,
        category_id: null,
      },
      categories: [],
    }
  },
  computed: {
    ...mapGetters('modal', [
      'modalDataID',
      'modalData',
      'modalActive',
      'refreshData',
    ]),
    codeError() {
      if (!this.$v.formData.code.$error) {
        return ''
      }

      if (!this.$v.formData.code.required) {
        return this.$tc('validation.required')
      }

      if (!this.$v.formData.code.minLength) {
        return this.$tc(
          'validation.code_min_length',
          this.$v.formData.code.$params.minLength.min,
          { count: this.$v.formData.code.$params.minLength.min }
        )
      }
    },
    nameError() {
      if (!this.$v.formData.name.$error) {
        return ''
      }

      if (!this.$v.formData.name.required) {
        return this.$tc('validation.required')
      }

      if (!this.$v.formData.name.minLength) {
        return this.$tc(
          'validation.name_min_length',
          this.$v.formData.name.$params.minLength.min,
          { count: this.$v.formData.name.$params.minLength.min }
        )
      }
    },
    descriptionError() {
      if (!this.$v.formData.description.$error) {
        return ''
      }
      if (!this.$v.formData.name.maxLength) {
        return this.$tc('validation.description_maxlength')
      }
    },
  },
  validations: {
    formData: {
      code: {
        required,
        minLength: minLength(3),
      },
      name: {
        required,
        minLength: minLength(3),
      },
      description: {
        maxLength: maxLength(255),
      },
    },
  },
  watch: {
    modalDataID(val) {
      if (val) {
        this.isEdit = true
        this.setData()
      } else {
        this.isEdit = false
      }
    },
    modalActive(val) {
      if (!this.modalActive) {
        this.resetFormData()
      }
    },
    master() {
      if (this.master) {
        this.formData.category_id = this.master.id
        return true
      }
    },
  },

  mounted() {
    if (this.modalDataID) {
      this.isEdit = true
      this.setData()
    }
  },

  created() {
    this.fetchInitData()
  },

  methods: {
    ...mapActions('modal', ['closeModal']),
    ...mapActions('subcategory', ['addSubcategory', 'updateSubcategory']),
    resetFormData() {
      this.formData = {
        id: null,
        code: null,
        name: null,
        description: null,
        category_id: null,
      }
      this.master = null
      this.$v.formData.$reset()
    },
    async submitSubCategoryData() {
      this.$v.formData.$touch()

      if (this.$v.$invalid) {
        return true
      }
      this.isLoading = true
      let response
      if (!this.isEdit) {
        response = await this.addSubcategory(this.formData)
      } else {
        response = await this.updateSubcategory(this.formData)
      }

      if (response.data) {
        if (!this.isEdit) {
          window.toastr['success'](
            this.$t('settings.expense_category.created_message')
          )
        } else {
          window.toastr['success'](
            this.$t('settings.expense_category.updated_message')
          )
        }
        window.hub.$emit('newSubcategory', response.data.subcategory)
        this.refreshData ? this.refreshData() : ''
        this.closeSubcategoryModal()
        this.isLoading = false
        return true
      }
      window.toastr['error'](response.data.error)
    },
    async setData() {
      this.formData = {
        id: this.modalData.id,
        code: this.modalData.code,
        name: this.modalData.name,
        description: this.modalData.description,
      }
      this.master = this.modalData.category
    },
    closeSubcategoryModal() {
      this.resetFormData()
      this.closeModal()
    },
    async fetchInitData() {
      let params = { limit: 'all' }
      let res = await window.axios.get('/api/v1/categories', { params })
      if (res) {
        this.categories = this.categories.concat(res.data.categories.data);
      }
    },
  },
}
</script>
